import { create } from 'zustand';
import type { CartLine } from './cart';
import type { CatalogItem, CatalogItemUnit } from '@matix/types';

/**
 * UI store — modal visibility, active tabs, transient screen state.
 *
 * Modals are tracked as a single `openModal` field rather than booleans per
 * modal. Only one modal is ever open at a time in the prototype, so this
 * matches behavior and prevents stacking glitches.
 */

export type ModalId =
  | 'modifier'
  | 'delivery'
  | 'deliveryConfirm' // Phase 9 — DeliveryConfirmModal (replaces payment in delivery mode)
  | 'payment'
  | 'override'      // legacy — kept for back-compat with any old callers
  | 'discount'      // Phase 8 — main discount modal
  | 'customerPicker'
  | 'tablePicker'
  | 'splitTransfer'        // Dine-in: Separate / Transfer items of a held order
  | 'tablesManual'         // Dine-in: "How tables work" — the manual chapter, in the till
  | 'recall'
  | 'zreport'
  | 'receipt'
  | 'cashMovement'         // Phase 10 — drop / expense / pickup
  | 'paymentMethodEdit'    // Phase 10 — change payment method (manager PIN)
  | 'membership'           // Membership — View Membership (redeem prepaid items)
  | 'stockBalance'         // Stock Balance report (legacy balance-scale icon)
  | 'sellGiftCard'         // Gift cards — add a card sale line to the cart
  | 'customerOrders'       // Orders (الطلبات) — the selected customer's previous receipts
  | null;

/**
 * Which workspace fills the shell. This is navigation only — it is NOT the
 * order type. The order type lives on the cart (`useCart().orderMode`) and is
 * switched with the topbar pills; 'walkin' here just means "the selling view"
 * (catalog + cart) whatever the current order mode is.
 *
 * A 'delivery' tab used to exist alongside 'walkin' and rendered a
 * "Delivery view — coming soon" placeholder. It duplicated the Delivery
 * order-mode pill and was removed.
 */
export type TabId =
  | 'walkin'
  | 'orders'
  | 'receipts'
  | 'reports'
  | 'offline'
  /**
   * Call-centre orders waiting at THIS branch. Its own workspace, not the
   * cart: a phone order must never be dropped into whatever the cashier is
   * ringing up at the counter, and it is settled here at hand-over.
   */
  | 'pending'
  /** This branch's call-centre monitoring dashboard. */
  | 'branchMonitor'
  /**
   * This branch's DELIVERY orders — from the till and from the phone alike.
   * Reads receipts (OrderType = 2), not `cc_order`, so it is NOT gated on the
   * call centre: a till-created delivery order has no `cc_order` row, and once
   * the kitchen serves it the ticket leaves the board with nowhere else for the
   * cashier to find it and take the money.
   */
  | 'delivery'
  /**
   * Store-to-store transfers: raise a request, receive one, print the slip.
   * Gated on the legacy "Create Transfer Transaction" permission.
   */
  | 'transfers'
  /**
   * The Delivery Manager (مدير التوصيل) — the legacy POS board /Delivery/Manager:
   * orders by delivery status, drivers, reports. Gated on the legacy
   * "Delivery Manger" permission (sic), like the legacy menu entry.
   */
  | 'deliveryManager'
  /**
   * The LAUNDRY counter (module `laundry`): orders taken in, ready, out and
   * collected today, with the order-barcode scan box.
   */
  | 'laundry'
  /**
   * EXPENSES — the legacy till's expense voucher (سند صرف), ported. Shown only
   * where the branch has expense types and an expense tender set up.
   */
  | 'expenses';

export type ScreenId = 'login' | 'shiftOpen' | 'app' | 'shiftClose';

interface UIState {
  screen: ScreenId;
  tab: TabId;
  openModal: ModalId;
  /**
   * Mobile-only: whether the cart drawer is slid in. On desktop/tablet the
   * cart is a permanent column and this flag is ignored by the layout. On
   * narrow screens (≤768px) the cart is a slide-in drawer toggled by the
   * floating "view order" bar — see CartPanel + the responsive block in
   * pos.css.
   */
  cartOpen: boolean;
  /** When opening modifier modal, this is the catalog item being modified. */
  modifierProduct: CatalogItem | null;
  /**
   * Display metadata for the modifier modal's resulting cart line — the
   * browsing category name (shown as the line's "station" label) plus the
   * resolved tile visual. Carried alongside modifierProduct because the
   * CatalogItem itself has no category-name or visual on it.
   */
  modifierMeta: {
    station: string;
    emoji: string;
    bg: string;
    /** The item's photo (data URL), so the resulting cart line shows it too. */
    image?: string | null;
    /**
     * The unit tile the cashier tapped, for a multi-unit item. The modal
     * prices and labels the resulting line in that unit; null = selling unit.
     */
    unit?: CatalogItemUnit | null;
    /**
     * Quantity to add once the modifiers are chosen. Set only on the
     * embedded-barcode path, where the weight came off the label rather than
     * from the cashier; everything else adds one.
     */
    qty?: number;
    /**
     * EDIT mode: the cart line whose options are being changed. The modal
     * opens on that line's choices and note, and updates it in place rather
     * than adding a line.
     */
    editCartId?: string;
  } | null;
  /** When opening recall modal, this is the parked order line. */
  recallTarget: { id: string; lines: CartLine[] } | null;

  setScreen: (s: ScreenId) => void;
  setTab: (t: TabId) => void;
  setCartOpen: (open: boolean) => void;
  toggleCart: () => void;
  showModal: (
    m: ModalId,
    payload?: {
      product?: CatalogItem;
      meta?: UIState['modifierMeta'];
      recallTarget?: UIState['recallTarget'];
    },
  ) => void;
  closeModal: () => void;
}

export const useUI = create<UIState>((set) => ({
  screen: 'login',
  tab: 'walkin',
  openModal: null,
  cartOpen: false,
  modifierProduct: null,
  modifierMeta: null,
  recallTarget: null,

  setScreen: (screen) => set({ screen }),
  // Leaving the selling view should also dismiss the mobile cart drawer so
  // it isn't still slid-in when the cashier returns to it.
  setTab: (tab) => set({ tab, cartOpen: false }),
  setCartOpen: (cartOpen) => set({ cartOpen }),
  toggleCart: () => set((s) => ({ cartOpen: !s.cartOpen })),

  showModal: (m, payload) =>
    set({
      openModal: m,
      modifierProduct: payload?.product ?? null,
      modifierMeta: payload?.meta ?? null,
      recallTarget: payload?.recallTarget ?? null,
    }),

  closeModal: () =>
    set({ openModal: null, modifierProduct: null, modifierMeta: null, recallTarget: null }),
}));
